import { AB_PIN_DETAIL_FULLSCREEN } from "@/constants/ABMng";
import useAppStore from "@/context/store";
import useABTest from "@/utils/hooks/useABtest";
import { isSafari } from "@/utils/isSafari";
import { isServer } from "@/utils/isServer";
import { useAsyncEffect } from "ahooks";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { ExcludeDestoryModal, destroyByInstance } from "../HBModal/ModalWrap";

export function usePinDetail() {
  const router = useRouter();
  const currentPin = useAppStore((s) => s.currentPin);
  const pinStack = useAppStore((s) => s.pinStack);
  const pinDetailFullscreen = useABTest(AB_PIN_DETAIL_FULLSCREEN);

  // 用来判断是否需要执行拦截操作
  const needInterceptRef = useRef(null);
  // 非全屏详情页：单弹窗实例
  const pinDetailIns = useRef(null);
  // 全屏详情页：多弹窗实例 Map，key 为 pinId
  const pinDetailInsMap = useRef(new Map());
  const isFull = pinDetailFullscreen.new;

  // 限制弹窗实例数量：只保留最新的3个
  const MAX_INSTANCES = !isServer() && isSafari() ? 3 : 5;

  useAsyncEffect(async () => {
    if (isFull) {
      // 全屏详情页：使用多弹窗堆栈逻辑
      const hasStack = pinStack && pinStack.length > 0;

      if (hasStack) {
        needInterceptRef.current = true;

        // 获取全屏弹窗组件
        const { PinDetailDialog } = await import("./modal@full");
        // 动态回收弹窗：清理不在当前 pinStack 中的弹窗
        const currentPinIds = new Set(pinStack.map((p) => p.pinId));
        const toDelete = [];

        const deleteModalInstance = () => {
          pinDetailInsMap.current.forEach((instance, pinId) => {
            if (!currentPinIds.has(pinId)) {
              toDelete.push(pinId);
            }
          });

          // 执行清理
          toDelete.forEach((pinId) => {
            const instance = pinDetailInsMap.current.get(pinId);
            if (instance) {
              destroyByInstance(instance.instance);
              pinDetailInsMap.current.delete(pinId);
            }
          });
        };

        deleteModalInstance();

        // 遍历 pinStack，确保每个 pin 都有对应的弹窗实例
        //pinStack.forEach((pinData, index) => {
        const pinData = pinStack[pinStack.length - 1];
        const index = pinStack.length - 1;
        const pinId = pinData.pinId;

        if (!pinDetailInsMap.current.has(pinId)) {
          // 创建新弹窗实例
          const instance = PinDetailDialog.open({
            name: ExcludeDestoryModal.pinDetail,
            pinData,
            stackIndex: index,
            zIndex: 10 + index,
            visible: index === pinStack.length - 1, // 只有最上层可见
            destroyOnClose: true,
            onClose: () => {
              // 关闭按钮：清空所有堆栈，回到原页面
              const { clearPinStack } = useAppStore.getState();
              const stackLength = pinStack?.length || 0;
              clearPinStack();
              if (stackLength > 0) {
                history.go(-1 * stackLength);
              }
            },
          });
          pinDetailInsMap.current.set(pinId, instance);
        } else {
          const instance = pinDetailInsMap.current.get(pinId);
          if (instance) {
            instance.update({
              visible: index === pinStack.length - 1,
              zIndex: 10 + index,
              stackIndex: index,
              pinData,
              onClose: () => {
                const { clearPinStack } = useAppStore.getState();
                const stackLength = pinStack?.length || 0;
                clearPinStack();
                if (stackLength > 0) {
                  history.go(-1 * stackLength);
                }
              },
            });
          }
        }

        if (pinDetailInsMap.current.size > MAX_INSTANCES) {
          const pinId = pinStack[pinStack.length - MAX_INSTANCES - 1].pinId;
          const instance = pinDetailInsMap.current.get(pinId);
          if (instance) {
            //instance.destroy();
            pinDetailInsMap.current.delete(pinId);
            destroyByInstance(instance.instance);
          }
        }
      } else {
        // 清空所有全屏弹窗
        pinDetailInsMap.current.forEach((instance) => {
          instance && destroyByInstance(instance.instance);
        });
        pinDetailInsMap.current.clear();
      }
    } else {
      // 非全屏详情页：使用原有的单弹窗逻辑
      if (currentPin) {
        needInterceptRef.current = true;

        if (!pinDetailIns.current) {
          // 获取非全屏弹窗组件
          const { PinDetailDialog } = await import("./modal");
          pinDetailIns.current = PinDetailDialog.open({
            // 标识弹窗名称, 全量销毁时，需要不关闭的弹窗
            name: ExcludeDestoryModal.pinDetail,
          });
        }
      } else {
        pinDetailIns.current?.destroy();
        pinDetailIns.current = null;
      }
    }
  }, [currentPin, pinStack, isFull]);

  useEffect(() => {
    /**
     * 这是为了解决页面如果有服务端渲染的方法，
     * 打开详情弹窗，再通过histor.back()这种方法返回的时候，会记录不了前置的滚动位置的问题
     */
    router.beforePopState(({ as, options }) => {
      if (isFull) {
        // 全屏详情页：使用多弹窗堆栈的路由拦截逻辑
        const state =
          typeof window !== "undefined" ? window.history.state : null;
        const isModalNavigation = state?.isModal || needInterceptRef?.current;

        // 如果当前有弹窗堆栈，且目标路由不是pin路由
        if (isModalNavigation && !/^\/pins\/\d+(\?.*)?$/.test(as)) {
          // 获取最新的 pinStack 状态
          const { pinStack: latestStack } = useAppStore.getState();

          if (latestStack && latestStack.length > 0) {
            // 还有弹窗，不应该让 router 改变 URL
            // 因为 popstate 已经正确处理了 pinStack，URL 应该保持为当前弹窗的路径
            // 使用 shallow 模式，阻止 router 的默认路由变化
            options.shallow = true;
            // 返回 false 阻止 router 的默认行为
            return false;
          } else {
            // 没有更多弹窗，清空堆栈并返回原页面
            const { clearPinStack } = useAppStore.getState();
            clearPinStack();
            needInterceptRef.current = false;
            // shallow为true则不执行服务端渲染的方法
            options.shallow = true;
            router.push(as, undefined, options);
            // 阻止路由更改
            return false;
          }
        } else if (isModalNavigation && /^\/pins\/\d+(\?.*)?$/.test(as)) {
          // 如果是弹窗内的 pin 详情页切换（包括图片预览），也使用 shallow 模式
          options.shallow = true;
          return true;
        } else {
          return true;
        }
      } else {
        // 非全屏详情页：使用原有的单弹窗路由拦截逻辑
        if (needInterceptRef?.current && !/^\/pins\/\d*$/.test(as)) {
          needInterceptRef.current = false;
          // shallow为true则不执行服务端渲染的方法
          options.shallow = true;
          router.push(as, undefined, options);
          // 阻止路由更改
          return false;
        } else {
          return true;
        }
      }
    });

    return () => {
      // 清理函数
    };
  }, [router, isFull]);
}
